import { type SigningType } from "@tezos-x/octez.connect-wallet";
import {
  type ImplicitAccount,
  type ImplicitOperations,
  estimate,
  getSigningTypeFromPayload,
  toAccountOperations,
  useChainRegistry,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  WcScenarioType,
  useAsyncActionHandler,
  useAvailableMultichainNetworks,
  useGetImplicitAccount,
  useGetOwnedAccountSafe,
  useValidateWcRequest,
  walletKit,
} from "@umami/state";
import { WalletConnectError, WcErrorCode, useCustomToast } from "@umami/utils";
import { formatJsonRpcError, formatJsonRpcResult } from "@walletconnect/jsonrpc-utils";
import { type SessionTypes, type SignClientTypes, type Verify } from "@walletconnect/types";
import { ethers } from "ethers";

import {
  validateAccount,
  validateEthAccountsParams,
  validateEthSendTransactionParams,
  validateEthSignTransactionParams,
  validateEthSignTypedDataParams,
  validateImplicitAccount,
  validateMessage,
  validateMultichainSupport,
  validateNetwork,
  validatePersonalSignParams,
  validateSession,
  validateWalletSwitchEthereumChainParams,
} from "./errorHandlers";
import { type WcHandlerDependenciess, type WcRequestParams } from "./type";
import { isTezosRequest } from "./utils";
import { SignPayloadRequestModal } from "../common/SignPayloadRequestModal";
import { RequestSignPage } from "../SendFlow/common/RequestSignPage";
import {
  type SdkSignPageProps,
  type SignHeaderProps,
  type SignPayloadProps,
} from "../SendFlow/utils";

const handleTezosGetAccounts = async (
  { id, topic }: WcRequestParams,
  { getImplicitAccount, toast }: WcHandlerDependenciess
) => {
  const session = validateSession(topic);
  const accountPkh = session.namespaces.tezos.accounts[0].split(":")[2];
  const signer = getImplicitAccount(accountPkh);
  const publicKey = signer.pk;
  const response = formatJsonRpcResult(id, [
    {
      algo: signer.curve,
      address: accountPkh,
      pubkey: publicKey,
    },
  ]);
  await walletKit.respondSessionRequest({ topic, response });

  toast({
    description: "Successfully provided the requested account data",
    status: "success",
  });
};

const handleEthAccounts = async (
  { id, topic, request, session }: WcRequestParams,
  { toast }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);
  validateEthAccountsParams(request.params, session);

  // Get all Ethereum and Etherlink accounts from the session
  const eip155Accounts = session.namespaces.eip155.accounts;
  const accounts = eip155Accounts.map(account => {
    // Extract the address from the account string (format: eip155:chainId:address)
    const address = account.split(":")[2];

    return address;
  });

  const response = formatJsonRpcResult(id, accounts);
  await walletKit.respondSessionRequest({ topic, response });

  toast({
    description: "Successfully provided the requested account data",
    status: "success",
  });
};

const handleTezosSign = (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  {
    getImplicitAccount,
    findNetworkByChainFullId,
    openWith,
    handleUserRejected,
  }: WcHandlerDependenciess
) => {
  if (!isTezosRequest(request.params)) {
    throw new WalletConnectError(
      "Invalid params format for tezos_sign",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const accountPkh = validateAccount(request.params.account, session);
  const signer = getImplicitAccount(accountPkh);
  void validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const signingType: SigningType = getSigningTypeFromPayload(request.params.payload!);
  const signPayloadProps: SignPayloadProps = {
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    payload: request.params.payload!,
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    signer,
    signingType,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };

  const modal = <SignPayloadRequestModal opts={signPayloadProps} />;
  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

const handleTezosSend = async (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  { getAccount, findNetworkByChainFullId, openWith, handleUserRejected }: WcHandlerDependenciess
) => {
  if (!isTezosRequest(request.params)) {
    throw new WalletConnectError(
      "Invalid params format for tezos_send",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const accountPkh = validateAccount(request.params.account, session);
  const account = getAccount(accountPkh);
  const signer = validateImplicitAccount(account, accountPkh, session);
  const network = validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const operation = toAccountOperations(request.params.operations, signer);
  const estimatedOperations = await estimate(operation, network);
  const headerProps: SignHeaderProps = {
    network,
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };
  const signProps: SdkSignPageProps = {
    headerProps: headerProps,
    operation: estimatedOperations,
  };

  const modal = <RequestSignPage {...signProps} />;
  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

const handlePersonalSign = async (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  {
    openWith,
    findNetworkByChainFullId,
    handleUserRejected,
    getImplicitAccount,
  }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);
  const params = validatePersonalSignParams(request.params, session);

  const messageToSign = params[0];
  const address = params[1];

  const accountPkh = validateAccount(address, session);
  const implicitAccount = getImplicitAccount(accountPkh);
  const signer = validateImplicitAccount(implicitAccount, accountPkh, session);

  validateMessage(messageToSign, session);

  const network = validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const headerProps: SignHeaderProps = {
    network,
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };

  const operation: ImplicitOperations = {
    type: "implicit",
    operations: [
      {
        type: "personal_sign",
        message: messageToSign.startsWith("0x")
          ? ethers.toUtf8String(messageToSign)
          : messageToSign,
      },
    ],
    sender: signer as ImplicitAccount,
    signer: signer as ImplicitAccount,
  };
  const signProps: SdkSignPageProps = {
    headerProps,
    operation: {
      ...operation,
      estimates: [],
    },
  };

  const modal = <RequestSignPage {...signProps} />;

  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

const handleEthSignTypedData = async (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  {
    openWith,
    findNetworkByChainFullId,
    handleUserRejected,
    getImplicitAccount,
  }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);
  const params = validateEthSignTypedDataParams(request.params, session);

  const address = params[0];
  const typedData = params[1];

  const accountPkh = validateAccount(address, session);
  const implicitAccount = getImplicitAccount(accountPkh);
  const signer = validateImplicitAccount(implicitAccount, accountPkh, session);

  const network = validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const headerProps: SignHeaderProps = {
    network,
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };

  const operation: ImplicitOperations = {
    type: "implicit",
    operations: [
      {
        type: "eth_signTypedData",
        typedData: JSON.parse(typedData),
        version: "v4",
      },
    ],
    sender: signer as ImplicitAccount,
    signer: signer as ImplicitAccount,
  };
  const signProps: SdkSignPageProps = {
    headerProps,
    operation: {
      ...operation,
      estimates: [],
    },
  };

  const modal = <RequestSignPage {...signProps} />;

  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

const handleEthSignTransaction = async (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  {
    openWith,
    findNetworkByChainFullId,
    handleUserRejected,
    getImplicitAccount,
  }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);
  const params = validateEthSignTransactionParams(request.params, session);

  const transaction = params[0];
  const address = transaction.from;

  const accountPkh = validateAccount(address, session);
  const implicitAccount = getImplicitAccount(accountPkh);
  const signer = validateImplicitAccount(implicitAccount, accountPkh, session);

  const network = validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const headerProps: SignHeaderProps = {
    network,
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };

  const operation: ImplicitOperations = {
    type: "implicit",
    operations: [
      {
        type: "eth_signTransaction",
        transaction,
      },
    ],
    sender: signer as ImplicitAccount,
    signer: signer as ImplicitAccount,
  };
  const signProps: SdkSignPageProps = {
    headerProps,
    operation: {
      ...operation,
      estimates: [],
    },
  };

  const modal = <RequestSignPage {...signProps} />;

  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

const handleEthSendTransaction = async (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  {
    getImplicitAccount,
    findNetworkByChainFullId,
    openWith,
    handleUserRejected,
  }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);
  const txParams = validateEthSendTransactionParams(request.params, session);

  const accountPkh = validateAccount(txParams.from, session);
  const signer = getImplicitAccount(accountPkh);

  const network = validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const headerProps: SignHeaderProps = {
    network,
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };

  const OPERATION_TYPE = txParams.data?.startsWith("0x095ea7b3")
    ? "eth_approve"
    : txParams.data?.startsWith("0xa9059cbb")
      ? "eth_transfer"
      : txParams.data?.startsWith("0xf513ef4c")
        ? "eth_placeOrder"
        : "eth_sendTransaction";

  const operation: ImplicitOperations = {
    type: "implicit",
    operations: [
      {
        type: OPERATION_TYPE,
        to: txParams.to,
        value: txParams.value || "0",
        data: txParams.data || "",
      },
    ],
    sender: signer,
    signer: signer,
  };

  const signProps: SdkSignPageProps = {
    headerProps,
    operation: {
      ...operation,
      estimates: [],
    },
  };

  const modal = <RequestSignPage {...signProps} />;
  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

const handleWalletSwitchEthereumChain = async (
  { id, topic, request, session }: WcRequestParams,
  { toast }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);
  const switchParams = validateWalletSwitchEthereumChainParams(request.params, session);

  const response = formatJsonRpcResult(id, null);
  await walletKit.respondSessionRequest({ topic, response });

  await walletKit.emitSessionEvent({
    topic,
    event: {
      name: "chainChanged",
      data: switchParams.chainId,
    },
    chainId: switchParams.chainId,
  });

  toast({
    description: `Switched to chain ${switchParams.chainId}`,
    status: "success",
  });
};

const handleEthSendRawTransaction = async (
  { id, topic, request, chainFullId, session, event }: WcRequestParams,
  { findNetworkByChainFullId, openWith, handleUserRejected }: WcHandlerDependenciess
) => {
  validateMultichainSupport(session, request.method);

  // Validate that we have the required parameters
  if (!Array.isArray(request.params) || request.params.length === 0) {
    throw new WalletConnectError(
      "Invalid params for eth_sendRawTransaction",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const rawTransaction = request.params[0];

  // Validate the raw transaction format
  if (typeof rawTransaction !== "string" || !rawTransaction.startsWith("0x")) {
    throw new WalletConnectError(
      "Invalid raw transaction format",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  // Decode the raw transaction to extract sender information
  let decodedTx;
  try {
    decodedTx = ethers.Transaction.from(rawTransaction);
  } catch (error) {
    console.log("Invalid raw transaction: unable to decode", error);

    throw new WalletConnectError(
      "Invalid raw transaction: unable to decode",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  // Extract the sender address from the decoded transaction
  const senderAddress = decodedTx.from;

  if (!senderAddress) {
    throw new WalletConnectError(
      "Invalid raw transaction: missing sender address",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  // Validate that the sender is in the session's accounts
  const sessionAccounts = session.namespaces.eip155.accounts;
  const isSenderInSession = sessionAccounts.some(account => {
    const accountAddress = account.split(":")[2];

    return accountAddress.toLowerCase() === senderAddress.toLowerCase();
  });

  if (!isSenderInSession) {
    throw new WalletConnectError(
      "Sender address not found in session accounts",
      WcErrorCode.INVALID_PARAMS,
      session
    );
  }

  const network = validateNetwork(chainFullId, session, findNetworkByChainFullId);

  const headerProps: SignHeaderProps = {
    network,
    appName: session.peer.metadata.name,
    appIcon: session.peer.metadata.icons[0],
    isScam: event.verifyContext.verified.isScam,
    validationStatus: event.verifyContext.verified.validation,
    requestId: { sdkType: "walletconnect", id: id, topic },
  };

  // Create account from the decoded transaction sender
  const account: ImplicitAccount = {
    type: "secret_key", // This is correct - it represents an imported/external account
    label: "Raw Transaction Sender",
    curve: "secp256k1",
    address: {
      type: "implicit",
      pkh: senderAddress,
    },
    pk: senderAddress, // For raw transactions, we don't have the private key, but we have the address
  };

  const operation: ImplicitOperations = {
    type: "implicit",
    operations: [
      {
        type: "eth_sendRawTransaction",
        rawTransaction,
      },
    ],
    sender: account,
    signer: account,
  };

  const signProps: SdkSignPageProps = {
    headerProps,
    operation: {
      ...operation,
      estimates: [],
    },
  };

  const modal = <RequestSignPage {...signProps} />;
  const onClose = () => {
    handleUserRejected();
  };

  return openWith(modal, { onClose });
};

/**
 * @returns a function that handles a beacon message and opens a modal with the appropriate content
 *
 * For operation requests it will also try to convert the operation(s) to our {@link Operation} format,
 * estimate the fee and open the BeaconSignPage only if it succeeds
 */
export const useHandleWcRequest = () => {
  const { openWith, goBack } = useDynamicModalContext();
  const { handleAsyncActionUnsafe } = useAsyncActionHandler();
  const validateWcRequest = useValidateWcRequest();
  const getAccount = useGetOwnedAccountSafe();
  const getImplicitAccount = useGetImplicitAccount();
  const { mainnet, testnet } = useAvailableMultichainNetworks();

  const findNetworkByChainFullId = (chainFullId: string) => {
    const availableNetworks = [...mainnet, ...testnet];
    const network = availableNetworks.find(
      network => `${network.namespace}:${network.identifier}` === chainFullId
    );

    return network;
  };

  const toast = useCustomToast();
  const chainRegistry = useChainRegistry();

  return async (
    event: {
      verifyContext: Verify.Context;
    } & SignClientTypes.BaseEventArgs<{
      request: {
        method: string;
        params: any;
        expiryTimestamp?: number;
      };
      chainId: string;
    }>,
    session: SessionTypes.Struct
  ) =>
    handleAsyncActionUnsafe(async () => {
      const { id, topic, params } = event;
      const { request, chainId: chainFullId } = params;

      const handleUserRejected = () => {
        if (validateWcRequest("request", id, WcScenarioType.REJECT, goBack)) {
          // dApp is waiting so we need to notify it
          const response = formatJsonRpcError(id, {
            code: WcErrorCode.USER_REJECTED,
            message: "User rejected the request",
          });
          console.info("WC request rejected by user", event, response);
          void walletKit.respondSessionRequest({ topic, response });
        }
      };

      const requestParams: WcRequestParams = {
        id,
        topic,
        request,
        chainFullId,
        session,
        event,
      };

      const dependencies: WcHandlerDependenciess = {
        getImplicitAccount,
        getAccount,
        findNetworkByChainFullId,
        openWith,
        handleUserRejected,
        toast,
        chainRegistry,
      };
      switch (request.method) {
        case "tezos_getAccounts":
          return handleTezosGetAccounts(requestParams, dependencies);
        case "tezos_sign":
          return await handleTezosSign(requestParams, dependencies);
        case "tezos_send":
          return await handleTezosSend(requestParams, dependencies);
        case "personal_sign":
          return await handlePersonalSign(requestParams, dependencies);
        case "eth_signTypedData":
          return await handleEthSignTypedData(requestParams, dependencies);
        case "eth_signTransaction":
          return await handleEthSignTransaction(requestParams, dependencies);
        case "eth_accounts":
        case "eth_requestAccounts": // Currently, behaves the same as eth_accounts, but can be extended to show a user different prompt if needed
          return await handleEthAccounts(requestParams, dependencies);
        case "eth_sendTransaction":
          return await handleEthSendTransaction(requestParams, dependencies);
        case "wallet_switchEthereumChain":
          return await handleWalletSwitchEthereumChain(requestParams, dependencies);
        case "eth_sendRawTransaction":
          return await handleEthSendRawTransaction(requestParams, dependencies);
        default:
          throw new WalletConnectError(
            `Unsupported method ${request.method}`,
            WcErrorCode.METHOD_UNSUPPORTED,
            session,
            request.method
          );
      }
    });
};
